import { useState, useMemo, RefObject } from 'react';
import { Button, Stack, Text, Group, ActionIcon, Select, Flex } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	Header,
	HeaderCloseBtn,
	HeaderTitle,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/SidePanels/styled';
import { RightPanelAction } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/types';
import { usePersonsInDeal } from '@dealstack/frontend/shared/components/Documents/hooks/usePersonsInDeal';
import { useAddPersonsToDocuments } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useAddPersonsToDocuments';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { ClassificationGroupType } from '@dealstack/shared/lib/backendClient';
import { useDocumentsWithFiltering } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useDocumentsWithFiltering';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useRemovePersonsFromDocuments } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useRemovePersonsFromDocuments';
import { useTranslationDocuments } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

interface Props {
	dealId: string;
	setRightPanelAction: (action: RightPanelAction) => void;
	allDocumentsSelected: boolean;
	documentIds: string[];
	documentsTableDataOperationsRef: RefObject<
		TableOperations<{
			relatedToIds: string[];
			updatedAt: { from: string; to: string };
			mipIds: string[];
		}>
	>;
	classificationId?: string;
	classificationGroup?: ClassificationGroupType;
}

interface Person {
	id: string;
	name: string;
}

export const EditPersonsToDocuments = ({
	dealId,
	setRightPanelAction,
	allDocumentsSelected,
	documentIds,
	documentsTableDataOperationsRef,
	classificationId,
	classificationGroup,
}: Props) => {
	const tDocuments = useTranslationDocuments();
	const { data: persons } = usePersonsInDeal(dealId);
	const { mutateAsync: addPersonsToDocuments, isPending: isAddingPersonsToDocuments } =
		useAddPersonsToDocuments();
	const { mutateAsync: removePersonsFromDocuments, isPending: isRemovingPersonsFromDocuments } =
		useRemovePersonsFromDocuments();

	const [selectedPersons, setSelectedPersons] = useState<Person[]>([]);
	const [searchValue, setSearchValue] = useState('');
	const [selectValue, setSelectValue] = useState<string | null>(null);

	const filteredOptions = useMemo(() => {
		if (!searchValue.trim()) {
			return persons || [];
		}

		const searchLower = searchValue.toLowerCase();
		return persons?.filter((person) => person.label.toLowerCase().includes(searchLower)) || [];
	}, [persons, searchValue]);

	const handleAddPerson = (personId: string) => {
		const person = persons?.find((p) => p.value === personId);
		if (person && !selectedPersons.find((p) => p.id === personId)) {
			setSelectedPersons((prev) => [
				...prev,
				{
					id: person.value,
					name: person.label,
				},
			]);
		}
		setSearchValue('');
		setSelectValue(null);
	};

	const handleRemovePerson = (personId: string) => {
		setSelectedPersons((prev) => prev.filter((p) => p.id !== personId));
	};

	const { refetch: refetchDocuments } = useDocumentsWithFiltering({
		dealId,
		dataOperations: documentsTableDataOperationsRef.current,
		classificationId,
		classificationGroup,
	});

	const handleAddPersonsToDocuments = async () => {
		await addPersonsToDocuments(
			{
				dealId,
				allDocumentsSelected,
				documentIds,
				personIds: selectedPersons.map((p) => p.id),
				filters: {
					searchQuery: documentsTableDataOperationsRef.current?.search,
					relatedToIds: documentsTableDataOperationsRef.current?.filter?.relatedToIds,
					classificationId,
					classificationGroup,
					dateRange: {
						from: documentsTableDataOperationsRef.current?.filter?.updatedAt?.from,
						to: documentsTableDataOperationsRef.current?.filter?.updatedAt?.to,
					},
				},
			},
			{
				onSuccess: async () => {
					await refetchDocuments();
					showToastV2({
						title: tDocuments('edit_persons_to_documents.toast.success.title'),
						message: tDocuments('edit_persons_to_documents.toast.success.message'),
						state: 'success',
					});
				},
				onError: () => {
					showToastV2({
						title: tDocuments('edit_persons_to_documents.toast.error.title'),
						message: tDocuments('edit_persons_to_documents.toast.error.message'),
						state: 'error',
					});
				},
			}
		);
		setRightPanelAction(RightPanelAction.NONE);
	};

	const handleRemovePersonsFromDocuments = async () => {
		await removePersonsFromDocuments(
			{
				dealId,
				allDocumentsSelected,
				documentIds,
				personIds: selectedPersons.map((p) => p.id),
				filters: {
					searchQuery: documentsTableDataOperationsRef.current?.search,
					relatedToIds: documentsTableDataOperationsRef.current?.filter?.relatedToIds,
					classificationId,
					classificationGroup,
					dateRange: {
						from: documentsTableDataOperationsRef.current?.filter?.updatedAt?.from,
						to: documentsTableDataOperationsRef.current?.filter?.updatedAt?.to,
					},
				},
			},
			{
				onSuccess: async () => {
					await refetchDocuments();
					showToastV2({
						title: tDocuments('edit_persons_to_documents.toast.success.title'),
						message: tDocuments('edit_persons_to_documents.toast.success.message'),
						state: 'success',
					});
				},
				onError: () => {
					showToastV2({
						title: tDocuments('edit_persons_to_documents.toast.error.title'),
						message: tDocuments('edit_persons_to_documents.toast.error.message'),
						state: 'error',
					});
				},
			}
		);
		setRightPanelAction(RightPanelAction.NONE);
	};

	const isDisabled =
		selectedPersons.length === 0 ||
		isAddingPersonsToDocuments ||
		isRemovingPersonsFromDocuments;

	return (
		<Stack gap="md" h="100%">
			<Header>
				<HeaderTitle>{tDocuments('edit_persons_to_documents.title')}</HeaderTitle>
				<HeaderCloseBtn onClick={() => setRightPanelAction(RightPanelAction.NONE)}>
					<Icon variant="close-v3" />
				</HeaderCloseBtn>
			</Header>
			<Stack gap="md" style={{ flex: 1 }}>
				<Stack gap="xs">
					<Text size="sm" fw={600}>
						{tDocuments('edit_persons_to_documents.person_name')}
					</Text>
					<Select
						name="person"
						placeholder={tDocuments(
							'edit_persons_to_documents.person_name_placeholder'
						)}
						data={filteredOptions}
						value={selectValue}
						searchValue={searchValue}
						onSearchChange={setSearchValue}
						onChange={handleAddPerson}
						searchable
						clearable
						rightSection={<Icon variant="caret-down" size="sm" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						nothingFoundMessage={tDocuments(
							'edit_persons_to_documents.no_persons_found'
						)}
					/>
				</Stack>

				<Stack gap="xs">
					<Text size="sm" fw={600}>
						{tDocuments('edit_persons_to_documents.related_people')}
					</Text>
					<Text size="xs" c="dimmed">
						{tDocuments('edit_persons_to_documents.related_people_description')}
					</Text>

					{selectedPersons.length > 0 && (
						<Stack gap="xs">
							{selectedPersons.map((person) => (
								<Group
									key={person.id}
									justify="apart"
									p="xs"
									style={{
										border: '1px solid #e9ecef',
										borderRadius: '4px',
										backgroundColor: '#f8f9fa',
									}}
								>
									<Text size="sm">{person.name}</Text>
									<ActionIcon
										size="sm"
										variant="subtle"
										color="red"
										onClick={() => handleRemovePerson(person.id)}
									>
										<Icon variant="close-v3" size="sm" />
									</ActionIcon>
								</Group>
							))}
						</Stack>
					)}
				</Stack>
			</Stack>
			<Flex gap="1rem" mt="auto" direction="column">
				<Button
					fullWidth
					onClick={handleAddPersonsToDocuments}
					disabled={isDisabled}
					loading={isAddingPersonsToDocuments}
				>
					{tDocuments('edit_persons_to_documents.add_button')}
				</Button>
				<Button
					fullWidth
					onClick={handleRemovePersonsFromDocuments}
					disabled={isDisabled}
					loading={isRemovingPersonsFromDocuments}
					color="red"
				>
					{tDocuments('edit_persons_to_documents.remove_button')}
				</Button>
			</Flex>
		</Stack>
	);
};
